import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import ProjectModal from './ProjectModal';

const { FiPlus, FiBriefcase, FiCheckSquare, FiSearch, FiArchive, FiMaximize2 } = FiIcons;

const Projects = () => {
  const navigate = useNavigate();
  const { projects, tasks } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('Active');

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-green-600 bg-green-100';
      case 'In Progress': return 'text-blue-600 bg-blue-100';
      case 'On Hold': return 'text-yellow-600 bg-yellow-100';
      case 'Planning': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (p.client || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesArchive = filter === 'Archived' ? p.isArchived : !p.isArchived;
    return matchesSearch && matchesArchive;
  });

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Projects</h1>
          <p className="text-slate-400 font-medium">Structure your business ventures and client work.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 text-white px-8 py-4 rounded-3xl font-black shadow-xl shadow-blue-100 flex items-center gap-3 hover:bg-blue-700 transition-all hover:-translate-y-1"
        >
          <SafeIcon icon={FiPlus} /> New Project
        </button>
      </div>

      <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4 items-center">
        <div className="flex-1 relative w-full">
          <SafeIcon icon={FiSearch} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" />
          <input 
            placeholder="Search projects by name or client..."
            className="w-full pl-14 pr-6 py-4 bg-slate-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          {['Active', 'Archived'].map(f => (
            <button 
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 md:flex-none px-6 py-4 rounded-2xl font-bold text-sm transition-all ${filter === f ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredProjects.map((project) => (
            <motion.div
              layout
              key={project.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 hover:shadow-xl hover:border-blue-100 transition-all flex flex-col group cursor-pointer"
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                    <SafeIcon icon={project.isArchived ? FiArchive : FiBriefcase} className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-900">{project.name}</h3>
                    <span className={`px-3 py-1 text-[10px] font-black rounded-full uppercase tracking-widest ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-slate-50 text-slate-400 group-hover:text-blue-600 rounded-xl transition-all">
                  <SafeIcon icon={FiMaximize2} />
                </div>
              </div>

              <p className="text-slate-400 text-sm font-medium mb-8 line-clamp-2">{project.description || 'No description provided.'}</p>

              <div className="space-y-3 mb-8 flex-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Client</span>
                  <span className="font-black text-slate-700">{project.client || 'Internal'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Billing</span>
                  <span className="font-black text-slate-700">{project.billingType}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Tasks</span>
                  <span className="font-black text-slate-700">
                    {tasks.filter(t => t.project === project.name).length} Items
                  </span>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-50">
                <div className="flex justify-between items-center mb-2 text-xs font-black uppercase tracking-widest text-slate-400">
                  <span>Project Deadline</span>
                  <span>{project.deadline ? new Date(project.deadline).toLocaleDateString() : 'None'}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-24 bg-white rounded-[3rem] border-2 border-dashed border-slate-100">
          <SafeIcon icon={FiBriefcase} className="text-6xl text-slate-100 mx-auto mb-4" />
          <p className="text-slate-400 font-bold text-xl">No projects found.</p>
          <p className="text-slate-300 font-medium">Create a new project to get started.</p>
        </div>
      )}

      <ProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </motion.div>
  );
};

export default Projects;